import { useState } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MeetingCalendarSection } from "@/components/agent/details/MeetingCalendarSection";
import { MeetingDetailsDrawer } from "@/components/agent/drawers/MeetingDetailsDrawer";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  useCancelGenieMeeting,
  useGenieMeetings,
  useGenieMeetingsRealtime,
  useLeaveGenieMeeting,
  useSendGenieToMeeting,
} from "@/hooks/agent/useGenieMeetings";
import {
  formatMeetingDuration,
  formatMeetingMoment,
  isCancellableMeeting,
  isLiveMeeting,
  isSupportedMeetingLink,
  meetingMoment,
  PROVIDER_LABELS,
  STATE_LABELS,
} from "@/lib/agent/genieMeetings";
import { Agent } from "@/types/agent/agent";
import { GenieMeeting } from "@/types/agent/genie-meeting";

interface MeetingsCardProps {
  agent: Agent;
  brand?: string;
}

type JoinMode = "now" | "later";

const INPUT_CLASS =
  "w-full min-h-[44px] px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm";
const FIELD_LABEL = "text-sm font-semibold text-gray-700 mb-2 block";
const MUTED = "#6B7280";
const DANGER = "#DC2626";
const ATTENDEE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// datetime-local wants local wall-clock time, without a zone.
const toLocalInputValue = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const stateColor = (meeting: GenieMeeting, brand: string): string => {
  if (meeting.state === "failed") return DANGER;
  if (meeting.state === "cancelled" || meeting.state === "scheduled") return MUTED;
  return brand;
};

export const MeetingsCard = ({ agent, brand = "#4E9CFF" }: MeetingsCardProps) => {
  const { data: meetings = [], isLoading } = useGenieMeetings(agent.id);
  useGenieMeetingsRealtime(agent.id);

  const sendMutation = useSendGenieToMeeting(agent.id);
  const cancelMutation = useCancelGenieMeeting(agent.id);
  const leaveMutation = useLeaveGenieMeeting();

  const [meetingLink, setMeetingLink] = useState("");
  const [title, setTitle] = useState("");
  const [joinMode, setJoinMode] = useState<JoinMode>("now");
  const [joinAt, setJoinAt] = useState("");
  const [emailNotes, setEmailNotes] = useState(false);
  const [attendees, setAttendees] = useState("");
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);

  const handleSend = async () => {
    const link = meetingLink.trim();
    if (!isSupportedMeetingLink(link)) {
      toast.error("Paste a Google Meet, Zoom or Microsoft Teams link.");
      return;
    }
    if (joinMode === "later" && !joinAt) {
      toast.error(`Pick when ${agent.name} should join.`);
      return;
    }
    const attendeeEmails = attendees.split(/[\s,;]+/).filter(Boolean);
    const notEmail = attendeeEmails.find((email) => !ATTENDEE_EMAIL_RE.test(email));
    if (notEmail) {
      toast.error(`"${notEmail}" isn't an email address.`);
      return;
    }

    try {
      const meeting = await sendMutation.mutateAsync({
        meetingLink: link,
        title: title.trim() || undefined,
        joinTime: joinMode === "later" ? new Date(joinAt).toISOString() : undefined,
        emailNotes,
        attendees: attendeeEmails.length ? attendeeEmails : undefined,
      });
      toast.success(
        meeting.join_time
          ? `${agent.name} will join on ${formatMeetingMoment(meeting.join_time)}`
          : `${agent.name} is on its way to the meeting`,
      );
      setMeetingLink("");
      setTitle("");
      setAttendees("");
      setJoinAt("");
      setJoinMode("now");
    } catch (err: any) {
      toast.error(err.message ?? `${agent.name} couldn't be sent to this meeting`);
    }
  };

  const handleCancel = async (meeting: GenieMeeting) => {
    try {
      await cancelMutation.mutateAsync(meeting.id);
      toast.success("Meeting cancelled");
    } catch (err: any) {
      toast.error(err.message ?? "This meeting couldn't be cancelled");
    }
  };

  const handleLeave = async (meeting: GenieMeeting) => {
    try {
      await leaveMutation.mutateAsync(meeting.id);
      toast.success(`${agent.name} is leaving. The notes will be ready in a few minutes.`);
    } catch (err: any) {
      toast.error(err.message ?? `${agent.name} couldn't leave the meeting`);
    }
  };

  const softButtonStyle = { backgroundColor: `${brand}15`, color: brand };
  const pendingActionId =
    (cancelMutation.isPending && cancelMutation.variables) || (leaveMutation.isPending && leaveMutation.variables) || null;

  return (
    <>
      <OverviewSectionCard
        title="Meetings"
        subtitle={`Send ${agent.name} to a meeting to take notes`}
        genieIcon="video"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
      >
        <div className="space-y-6">
          <div className="border-b pb-6" style={{ borderColor: `${brand}15` }}>
            <MeetingCalendarSection agent={agent} brand={brand} />
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="meeting-link" className={FIELD_LABEL}>
                Meeting link
              </label>
              <input
                id="meeting-link"
                type="url"
                inputMode="url"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
                autoComplete="off"
                className={INPUT_CLASS}
              />
              <p className="text-xs text-gray-500 mt-2">
                Works with Google Meet, Zoom and Microsoft Teams. {agent.name} joins as a guest, tells everyone it is
                taking notes, and brings back a summary, action items and the transcript.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="meeting-title" className={FIELD_LABEL}>
                  What it's about (optional)
                </label>
                <input
                  id="meeting-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Weekly planning"
                  maxLength={120}
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </div>

              <div>
                <span className={FIELD_LABEL}>When</span>
                <div className="flex gap-2">
                  {(["now", "later"] as JoinMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setJoinMode(mode)}
                      aria-pressed={joinMode === mode}
                      className="min-h-[44px] px-4 rounded-xl text-sm font-medium border transition-colors"
                      style={
                        joinMode === mode
                          ? { ...softButtonStyle, borderColor: `${brand}55` }
                          : { color: MUTED, borderColor: "#E5E7EB" }
                      }
                    >
                      {mode === "now" ? "Right now" : "At a set time"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="meeting-attendees" className={FIELD_LABEL}>
                Who's attending (optional)
              </label>
              <input
                id="meeting-attendees"
                type="text"
                inputMode="email"
                value={attendees}
                onChange={(e) => setAttendees(e.target.value)}
                placeholder="sarah@acme.com, frank@acme.com"
                autoComplete="off"
                className={INPUT_CLASS}
              />
              <p className="text-xs text-gray-500 mt-2">
                Their emails let {agent.name} file the notes against the right people, for example in your CRM through
                HG Sync.
              </p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <label htmlFor="meeting-email-notes" className="min-w-0 cursor-pointer">
                <span className="text-sm font-semibold text-gray-700 block">Email the notes</span>
                <span className="text-xs text-gray-500 block mt-0.5">
                  When the meeting ends, the summary and action items go to {agent.name}'s owner by email.
                </span>
              </label>
              <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
                <Switch id="meeting-email-notes" checked={emailNotes} onCheckedChange={setEmailNotes} />
              </div>
            </div>

            {joinMode === "later" && (
              <div className="sm:max-w-xs">
                <label htmlFor="meeting-join-at" className={FIELD_LABEL}>
                  Join at
                </label>
                <input
                  id="meeting-join-at"
                  type="datetime-local"
                  value={joinAt}
                  min={toLocalInputValue(new Date())}
                  onChange={(e) => setJoinAt(e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
            )}

            <Button
              type="button"
              onClick={handleSend}
              disabled={sendMutation.isPending || !meetingLink.trim()}
              className="min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
            >
              {sendMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {sendMutation.isPending
                ? `Sending ${agent.name}...`
                : joinMode === "now"
                  ? `Send ${agent.name} now`
                  : `Schedule ${agent.name}`}
            </Button>
          </div>

          <div className="border-t pt-5" style={{ borderColor: `${brand}15` }}>
            {isLoading ? (
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
                <span className="text-sm text-gray-600">Loading meetings...</span>
              </div>
            ) : meetings.length === 0 ? (
              <div className="text-center py-6">
                <p className="font-medium text-gray-900 mb-1">No meetings yet</p>
                <p className="text-sm text-gray-600">
                  Paste a link above and {agent.name} will join, listen, and bring the notes back here.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {meetings.map((meeting) => {
                  const color = stateColor(meeting, brand);
                  const duration = formatMeetingDuration(meeting.duration_secs);
                  const isActing = pendingActionId === meeting.id;

                  return (
                    <li
                      key={meeting.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-gray-200 bg-white transition-colors hover:border-gray-300"
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedMeetingId(meeting.id)}
                        className="flex-1 min-w-0 min-h-[44px] flex items-center gap-3 px-4 py-3 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {meeting.title || PROVIDER_LABELS[meeting.meeting_provider]}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {[
                              meeting.title ? PROVIDER_LABELS[meeting.meeting_provider] : null,
                              formatMeetingMoment(meetingMoment(meeting)),
                              duration,
                              meeting.calendar_event_id ? "From your calendar" : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                        <span
                          className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-white shadow-sm border"
                          style={{ color, borderColor: `${color}55` }}
                        >
                          {isLiveMeeting(meeting) && (
                            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: color }} />
                          )}
                          {STATE_LABELS[meeting.state]}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                      </button>

                      {(isCancellableMeeting(meeting) || meeting.state === "in_meeting") && (
                        <button
                          type="button"
                          onClick={() => (meeting.state === "in_meeting" ? handleLeave(meeting) : handleCancel(meeting))}
                          disabled={isActing}
                          className="shrink-0 min-h-[44px] mx-4 mb-3 sm:m-0 sm:mr-3 px-3 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                          style={softButtonStyle}
                        >
                          {isActing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          {meeting.state === "in_meeting" ? "Leave meeting" : "Cancel"}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </OverviewSectionCard>

      <MeetingDetailsDrawer genie={agent} meetingId={selectedMeetingId} onClose={() => setSelectedMeetingId(null)} />
    </>
  );
};
